import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { humanReadableMs } from '@flakiness/shared/common/utils.js';
import { css, html, LitElement, nothing, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { linkStyles } from './cssstyles.js';

@customElement('job-details')
export class JobDetails extends LitElement {
  @property({ attribute: false }) job?: WireTypes.Job;

  private _renderTimestamp(seconds: number): TemplateResult {
    const iso = new Date(seconds * 1000).toISOString();
    return html`
      <sl-format-date month="short" day="numeric" year="numeric" hour="numeric" minute="numeric" hour-format="12" date=${iso}></sl-format-date>
      <span class=muted>(<sl-relative-time date=${iso}></sl-relative-time>)</span>
    `;
  }

  private _renderData(data: any): TemplateResult {
    if (data === undefined || data === null)
      return html`<span class=muted>No data</span>`;
    if (typeof data === 'object') {
      if (Object.keys(data).length === 0)
        return html`<span class=muted>No data</span>`;
      return html`<json-viewer .json=${data}></json-viewer>`;
    }
    return html`<span class=mono>${JSON.stringify(data)}</span>`;
  }

  render() {
    const job = this.job;
    if (!job)
      return nothing;

    const rows: TemplateResult[] = [];
    const row = (label: string, value: TemplateResult|string) =>
      rows.push(html`<div class=label>${label}</div><div class=value>${value}</div>`);

    row('Job ID', html`<span class=mono>${job.jobId}</span>`);
    row('Internal ID', html`<span class=mono>${job.id}</span>`);
    row('Queue', job.queueName);
    if (job.category)
      row('Category', job.category);
    if (job.concurrencyId)
      row('Concurrency', html`<span class=mono>${job.concurrencyId}</span>`);
    if (job.workerName)
      row('Worker', html`<span class=mono>${job.workerName}</span>`);
    if (job.executionResult) {
      const duration = job.executionDurationSeconds !== undefined ? ` (${humanReadableMs(job.executionDurationSeconds * 1000)})` : '';
      row('Result', `${job.executionResult}${duration}`);
    }
    if (job.retry > 0)
      row('Retry', `#${job.retry}`);
    row('Priority', `${job.priority}`);
    row('Submitted', this._renderTimestamp(job.submittedTimestampSeconds));
    if (job.executionTimestampSeconds)
      row('Executed', this._renderTimestamp(job.executionTimestampSeconds));
    if (job.heartbeatTimestampSeconds)
      row('Heartbeat', this._renderTimestamp(job.heartbeatTimestampSeconds));
    if (job.executeAfterTimestampSeconds)
      row('Execute after', this._renderTimestamp(job.executeAfterTimestampSeconds));

    return html`
      <div class=container>
        ${job.executionError ? html`
          <div class=error-block>
            <div class=error-title><sl-icon name=exclamation-triangle-fill></sl-icon> Error</div>
            <pre class=error-body>${job.executionError}</pre>
          </div>
        ` : nothing}
        <div class=section-title>Details</div>
        <div class=grid>${rows}</div>
        <div class=section-title>Data</div>
        ${this._renderData(job.data)}
      </div>
    `;
  }

  static styles = [linkStyles, css`
    :host {
      display: block;
    }

    .container {
      display: flex;
      flex-direction: column;
      gap: var(--sl-spacing-medium);
    }

    .section-title {
      font-size: var(--sl-font-size-x-small);
      font-weight: var(--sl-font-weight-bold);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--sl-color-neutral-500);
    }

    .grid {
      display: grid;
      grid-template-columns: max-content 1fr;
      gap: var(--sl-spacing-2x-small) var(--sl-spacing-large);
      align-items: baseline;
      font-size: var(--sl-font-size-small);
    }

    .label {
      color: var(--sl-color-neutral-500);
      white-space: nowrap;
    }

    .value {
      min-width: 0;
      overflow-wrap: anywhere;
    }

    .mono {
      font-family: var(--sl-font-mono);
      font-size: var(--sl-font-size-small);
    }

    .muted {
      color: var(--sl-color-neutral-500);
    }

    .error-block {
      background-color: var(--sl-color-danger-50);
      border: 1px solid var(--sl-color-danger-200);
      border-radius: var(--sl-border-radius-medium);
      padding: var(--sl-spacing-small) var(--sl-spacing-medium);
    }

    .error-title {
      display: flex;
      align-items: center;
      gap: var(--sl-spacing-2x-small);
      color: var(--sl-color-danger-700);
      font-weight: var(--sl-font-weight-semibold);
      margin-bottom: var(--sl-spacing-2x-small);
    }

    .error-body {
      margin: 0;
      white-space: pre-wrap;
      overflow-wrap: anywhere;
      overflow: auto;
      max-height: 20em;
      font-family: var(--sl-font-mono);
      font-size: var(--sl-font-size-small);
      color: var(--sl-color-danger-800);
    }
  `];
}
